/**
 * Input Settings modal.
 *
 * Three sections:
 *  1. ESP32 WebSocket buttons — one URL per line, live presence dots.
 *     Protocol: ws://<host>:81/  emitting { t:"hello"|"btn"|"hb", id, ... }
 *     Compatible with mcu/m5atom-button/src/main.cpp.
 *  2. Gamepad — lists connected gamepads (read from navigator.getGamepads()).
 *  3. Keyboard shortcuts — static cheat-sheet.
 *
 * Config is persisted in localStorage under 'shk.espButtons.v1' (the same
 * key EspButtonClient reads) so the running game picks up changes on the
 * next save without a page reload.  A note at the bottom reminds the user
 * to reload for changes to take effect in the live physics session.
 */

import { createModalShell } from './modalUtils.js';

export interface InputModalController {
  close(): void;
}

export interface InputModalOptions {
  /** Fired whenever the saved URL list changes so the caller can update the
   *  live EspButtonClient pool if desired. */
  onUrlsChanged?: (urls: string[]) => void;
  onClose?: () => void;
}

// Storage key — must match EspButtonClient.ts readEspButtonUrls().
const STORAGE_KEY = 'shk.espButtons.v1';

function readUrls(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return [];
    return raw.filter((u): u is string => typeof u === 'string' && u.startsWith('ws'));
  } catch { return []; }
}

function saveUrls(urls: string[]): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(urls)); } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

function css(el: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
  Object.assign(el.style, styles);
}

function section(title: string): HTMLDivElement {
  const wrap = document.createElement('div');
  css(wrap, { display: 'flex', flexDirection: 'column', gap: '8px' });
  const h = document.createElement('div');
  h.textContent = title;
  css(h, { fontWeight: '700', fontSize: '12px', letterSpacing: '0.1em', opacity: '0.7', textTransform: 'uppercase' });
  wrap.appendChild(h);
  return wrap;
}



// ---------------------------------------------------------------------------
// Presence probe: open a WebSocket, read one hello frame, close.
// Returns the device id if successful, null on timeout.
// ---------------------------------------------------------------------------

interface ProbeResult { online: boolean; deviceId: string | null; fw: string | null }

function probeUrl(url: string, timeoutMs = 3500): Promise<ProbeResult> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (r: ProbeResult) => { if (!done) { done = true; resolve(r); } };
    const timer = setTimeout(() => { ws.close(); finish({ online: false, deviceId: null, fw: null }); }, timeoutMs);
    let ws: WebSocket;
    try { ws = new WebSocket(url); } catch { clearTimeout(timer); finish({ online: false, deviceId: null, fw: null }); return; }
    ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as Record<string, unknown>;
        if (msg['t'] === 'hello') {
          clearTimeout(timer);
          ws.close();
          finish({ online: true, deviceId: String(msg['id'] ?? ''), fw: msg['fw'] ? String(msg['fw']) : null });
        }
      } catch { /* ignore */ }
    });
    ws.addEventListener('error', () => { clearTimeout(timer); finish({ online: false, deviceId: null, fw: null }); });
    ws.addEventListener('close', () => { clearTimeout(timer); finish({ online: false, deviceId: null, fw: null }); });
  });
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function openInputModal(opts: InputModalOptions = {}): InputModalController {
  const shell = createModalShell({
    width: 'min(520px, 96vw)',
    backdrop: 'radial-gradient(circle at 50% 30%, rgba(20,60,120,0.4), rgba(0,0,0,0.75))',
    panelBackground: 'linear-gradient(180deg, #0e1e30 0%, #050d18 100%)',
    panelBorder: '1px solid #2a5a9a',
    ...(opts.onClose ? { onClose: opts.onClose } : {}),
  });
  css(shell.panel, {
    padding: '18px', gap: '16px', borderRadius: '14px',
    boxShadow: '0 8px 40px rgba(40,120,255,0.2)',
    maxHeight: '90vh', overflowY: 'auto',
  });

  // ---- Header --------------------------------------------------------------
  const header = document.createElement('div');
  css(header, { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' });
  const heading = document.createElement('div');
  heading.textContent = 'INPUT SETTINGS';
  css(heading, { fontWeight: '800', fontSize: '20px', letterSpacing: '0.12em' });
  const reloadNote = document.createElement('div');
  reloadNote.textContent = 'reload page to apply';
  css(reloadNote, { fontSize: '10px', opacity: '0.45' });
  header.append(heading, reloadNote);
  shell.panel.appendChild(header);

  // ==========================================================================
  // Section 1: ESP32 WebSocket buttons
  // ==========================================================================
  const espSection = section('ESP32 Hardware Buttons');
  shell.panel.appendChild(espSection);

  const espDesc = document.createElement('div');
  espDesc.textContent =
    'One WebSocket URL per line — ws://hostname:81/ or ws://IP:81/. ' +
    'Devices running the stickhook-button firmware (port 81) connect automatically on reload.';
  css(espDesc, { fontSize: '12px', opacity: '0.6', lineHeight: '1.5' });
  espSection.appendChild(espDesc);

  // URL textarea
  const textarea = document.createElement('textarea');
  textarea.value = readUrls().join('\n');
  textarea.rows = 4;
  textarea.placeholder = 'ws://stickhook-4760a1.local:81/\nws://192.168.1.55:81/';
  textarea.spellcheck = false;
  css(textarea, {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(80,160,255,0.35)',
    borderRadius: '8px', padding: '10px',
    color: '#ddeeff', fontFamily: 'monospace', fontSize: '12px',
    resize: 'vertical', outline: 'none',
  });
  espSection.appendChild(textarea);

  // Button row: Save + probe results
  const btnRow = document.createElement('div');
  css(btnRow, { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' });

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.textContent = 'Save';
  css(saveBtn, {
    padding: '7px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    background: '#1a6be0', color: '#fff', fontWeight: '700', fontSize: '13px',
  });
  btnRow.appendChild(saveBtn);

  const testBtn = document.createElement('button');
  testBtn.type = 'button';
  testBtn.textContent = 'Test connections';
  css(testBtn, {
    padding: '7px 18px', borderRadius: '8px', cursor: 'pointer',
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
    color: '#ccc', fontSize: '13px',
  });
  btnRow.appendChild(testBtn);

  espSection.appendChild(btnRow);

  // Probe results list
  const probeList = document.createElement('div');
  css(probeList, { display: 'flex', flexDirection: 'column', gap: '6px' });
  espSection.appendChild(probeList);

  function parseTextarea(): string[] {
    return textarea.value
      .split('\n')
      .map(s => s.trim())
      .filter(s => s.startsWith('ws'));
  }

  saveBtn.addEventListener('click', () => {
    const urls = parseTextarea();
    saveUrls(urls);
    opts.onUrlsChanged?.(urls);
    // Flash the button green briefly for confirmation.
    const orig = saveBtn.style.background;
    saveBtn.style.background = '#1a8e40';
    saveBtn.textContent = 'Saved!';
    setTimeout(() => { saveBtn.style.background = orig; saveBtn.textContent = 'Save'; }, 1200);
  });

  function renderProbeRow(url: string, result?: ProbeResult): HTMLDivElement {
    const row = document.createElement('div');
    css(row, {
      display: 'flex', alignItems: 'center', gap: '10px',
      background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '8px 10px',
    });

    // Dot
    const dot = document.createElement('div');
    css(dot, {
      width: '10px', height: '10px', borderRadius: '50%', flexShrink: '0',
      background: result === undefined ? '#888' : result.online ? '#22cc66' : '#cc3333',
      boxShadow: result?.online ? '0 0 6px #22cc66' : 'none',
    });
    row.appendChild(dot);

    // URL + info
    const info = document.createElement('div');
    css(info, { display: 'flex', flexDirection: 'column', gap: '2px', flex: '1', minWidth: '0' });
    const urlEl = document.createElement('div');
    urlEl.textContent = url;
    css(urlEl, { fontFamily: 'monospace', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
    info.appendChild(urlEl);
    if (result && result.online && result.deviceId) {
      const sub = document.createElement('div');
      sub.textContent = result.deviceId + (result.fw ? `  fw ${result.fw}` : '');
      css(sub, { fontSize: '11px', opacity: '0.55' });
      info.appendChild(sub);
    } else if (result && !result.online) {
      const sub = document.createElement('div');
      sub.textContent = 'unreachable';
      css(sub, { fontSize: '11px', color: '#cc6655' });
      info.appendChild(sub);
    }
    row.appendChild(info);

    // Identify button (only when online)
    if (result?.online) {
      const idBtn = document.createElement('button');
      idBtn.type = 'button';
      idBtn.textContent = '⚡ Identify';
      css(idBtn, {
        padding: '4px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)',
        background: 'rgba(255,255,255,0.07)', cursor: 'pointer', fontSize: '11px', color: '#ddd',
        flexShrink: '0',
      });
      idBtn.addEventListener('click', () => {
        try {
          const sock = new WebSocket(url);
          sock.addEventListener('open', () => {
            sock.send(JSON.stringify({ t: 'identify' }));
            setTimeout(() => sock.close(), 500);
          });
        } catch { /* unreachable — button wouldn't show */ }
      });
      row.appendChild(idBtn);
    }

    return row;
  }

  testBtn.addEventListener('click', async () => {
    const urls = parseTextarea();
    probeList.innerHTML = '';
    if (urls.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No URLs to test.';
      css(empty, { fontSize: '12px', opacity: '0.5' });
      probeList.appendChild(empty);
      return;
    }
    testBtn.disabled = true;
    testBtn.textContent = 'Testing…';

    // Render pending rows immediately.
    const rows: HTMLDivElement[] = urls.map(u => {
      const r = renderProbeRow(u);
      probeList.appendChild(r);
      return r;
    });

    // Probe all in parallel; update each row as results arrive.
    await Promise.all(urls.map(async (url, i) => {
      const result = await probeUrl(url);
      const fresh = renderProbeRow(url, result);
      probeList.replaceChild(fresh, rows[i]!);
    }));

    testBtn.disabled = false;
    testBtn.textContent = 'Test connections';
  });

  // ==========================================================================
  // Section 2: Gamepads
  // ==========================================================================
  const gpSection = section('Gamepad');
  shell.panel.appendChild(gpSection);

  const gpList = document.createElement('div');
  css(gpList, { display: 'flex', flexDirection: 'column', gap: '6px' });
  gpSection.appendChild(gpList);

  function refreshGamepads(): void {
    gpList.innerHTML = '';
    const pads = navigator.getGamepads ? [...navigator.getGamepads()].filter(Boolean) : [];
    if (pads.length === 0) {
      const none = document.createElement('div');
      none.textContent = 'No gamepads detected. Press a button on your controller to activate it.';
      css(none, { fontSize: '12px', opacity: '0.55', lineHeight: '1.5' });
      gpList.appendChild(none);
    } else {
      for (const pad of pads) {
        if (!pad) continue;
        const row = document.createElement('div');
        css(row, {
          display: 'flex', alignItems: 'center', gap: '10px',
          background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '8px 10px',
        });
        const dot = document.createElement('div');
        css(dot, { width: '10px', height: '10px', borderRadius: '50%', background: '#22cc66', boxShadow: '0 0 6px #22cc66', flexShrink: '0' });
        row.appendChild(dot);
        const info = document.createElement('div');
        css(info, { display: 'flex', flexDirection: 'column', gap: '2px', flex: '1' });
        const name = document.createElement('div');
        name.textContent = pad.id;
        css(name, { fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
        const detail = document.createElement('div');
        detail.textContent = `${pad.buttons.length} buttons · ${pad.axes.length} axes · mapping: ${pad.mapping || 'none'}`;
        css(detail, { fontSize: '11px', opacity: '0.5' });
        info.append(name, detail);
        row.appendChild(info);
        gpList.appendChild(row);
      }
    }
  }
  refreshGamepads();

  const refreshBtn = document.createElement('button');
  refreshBtn.type = 'button';
  refreshBtn.textContent = 'Refresh';
  css(refreshBtn, {
    padding: '6px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.07)', cursor: 'pointer', fontSize: '12px', color: '#ccc',
    alignSelf: 'flex-start',
  });
  refreshBtn.addEventListener('click', refreshGamepads);
  gpSection.appendChild(refreshBtn);

  const gpHint = document.createElement('div');
  gpHint.textContent = 'Any face button / trigger: grapple · Start: pause · D-pad: menu navigation · A: select';
  css(gpHint, { fontSize: '11px', opacity: '0.5', lineHeight: '1.6' });
  gpSection.appendChild(gpHint);

  // ==========================================================================
  // Section 3: Keyboard shortcuts
  // ==========================================================================
  const kbSection = section('Keyboard');
  shell.panel.appendChild(kbSection);

  const shortcuts: [string, string][] = [
    ['Space / hold', 'Grapple (hold to attach, release to let go)'],
    ['P  /  Esc', 'Pause / resume'],
    ['R', 'Restart level'],
    ['N', 'Next level'],
    ['F', 'Toggle fullscreen'],
    ['E', 'Open level editor'],
    ['↑ ↓ ← →', 'Navigate pause menu'],
    ['Enter', 'Select pause menu item'],
  ];
  const table = document.createElement('table');
  css(table, { borderCollapse: 'collapse', width: '100%', fontSize: '12px' });
  for (const [key, desc] of shortcuts) {
    const tr = document.createElement('tr');
    const td1 = document.createElement('td');
    td1.textContent = key;
    css(td1, {
      padding: '4px 10px 4px 0', whiteSpace: 'nowrap', fontFamily: 'monospace',
      color: '#aad4ff', verticalAlign: 'top',
    });
    const td2 = document.createElement('td');
    td2.textContent = desc;
    css(td2, { padding: '4px 0', opacity: '0.75', verticalAlign: 'top' });
    tr.append(td1, td2);
    table.appendChild(tr);
  }
  kbSection.appendChild(table);

  document.body.appendChild(shell.overlay);

  return { close: shell.close };
}
